import React from 'react'

import type { IconNames } from '@gousto-internal/citrus-react'
import {
  AlignItems,
  Body2,
  Box,
  Color,
  Display,
  DisplayPosition,
  Divider,
  FlexDirection,
  FontFamily,
  Icon,
  IconVariant,
  JustifyContent,
  Space,
  Text,
} from '@gousto-internal/citrus-react'
import classnames from 'classnames'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'

import { actionTypes } from 'actions/actionTypes'
import { boxSummaryVisibilityChange } from 'actions/boxSummary'
import { editBoxSizeOrDate } from 'actions/trackingKeys'
import { getBasketDate, getBasketRecipesCount, getNumPortions } from 'selectors/basket'

import { BoxSummaryPickerContainer } from './BoxSummaryPickerContainer'
import type { BoxSummaryProps } from './__config__/boxTypes'
import { boxTypes } from './__config__/boxTypes'

import css from '../CollectionsNav.css'

const formatDateToHumanReadable = (dateString: string) => moment(dateString).format('ddd D MMM')

export const trackEditBoxSizeOrDate = (currentSelection: string) => ({
  type: actionTypes.TRACKING,
  trackingData: {
    actionType: editBoxSizeOrDate,
    currentSelection,
  },
})

const BoxSummaryItem = ({ iconName, itemTitle, dataNoCode }: BoxSummaryProps) => {
  return (
    <>
      <Space direction="vertical" size={2} />
      <Box
        paddingH={2}
        paddingV={2}
        display={Display.Flex}
        flexDirection={FlexDirection.Row}
        alignItems={AlignItems.Center}
      >
        <Icon name={iconName as IconNames} />
        <Space direction="horizontal" size={2} />
        <Body2 data-no-code={dataNoCode}>{itemTitle}</Body2>
      </Box>
    </>
  )
}

const BoxSummaryEditItem = () => (
  <Box
    data-no-code="menuConfigEdit"
    paddingH={2}
    paddingV={2}
    paddingRight={4}
    paddingBottom={2}
    display={Display.Flex}
    flexDirection={FlexDirection.Row}
    alignItems={AlignItems.Center}
  >
    <Icon size={4} variant={IconVariant.Actionable} name="edit" />
    <Text
      data-no-code="menuConfigEditLabel"
      fontFamily={FontFamily.Bold}
      size={[1, null, 3]}
      underline
      color={Color.Secondary_400}
    >
      edit
    </Text>
  </Box>
)

const BoxSummaryBasketItem = ({ basketRecipesCount }: { basketRecipesCount: number }) => (
  <Box
    data-no-code="menuConfigBasket"
    data-testid="box-summary-basket"
    paddingH={2}
    paddingV={2}
    paddingRight={4}
    paddingBottom={2}
    display={Display.Flex}
    flexDirection={FlexDirection.Row}
    alignItems={AlignItems.Center}
  >
    <Box position={DisplayPosition.Relative}>
      <Icon size={6} variant={IconVariant.Actionable} name="shopping_basket" />

      {basketRecipesCount > 0 && (
        <Box
          data-testid="box-summary-basket-count"
          position={DisplayPosition.Absolute}
          top="-6px"
          right="-6px"
          bg={Color.Secondary_600}
          display={Display.Flex}
          alignItems={AlignItems.Center}
          justifyContent={JustifyContent.Center}
          height="18px"
          width="18px"
          paddingH={1}
          borderRadius="round"
        >
          <Text color={Color.White} fontFamily={FontFamily.Bold} size={0}>
            {basketRecipesCount}
          </Text>
        </Box>
      )}
    </Box>
  </Box>
)

type BoxSummaryPickerProps = {
  isBasketComprehensionEnabled?: boolean
}

export const BoxSummaryPicker = ({ isBasketComprehensionEnabled }: BoxSummaryPickerProps) => {
  const dispatch = useDispatch()
  const numPortions = useSelector(getNumPortions)
  const basketDate = useSelector(getBasketDate)
  const basketRecipesCount = useSelector(getBasketRecipesCount)

  const { title } = boxTypes[numPortions]

  const formattedBasketDate = formatDateToHumanReadable(basketDate)

  const showBoxSummary = () => {
    dispatch(boxSummaryVisibilityChange(true))
    dispatch(trackEditBoxSizeOrDate(title))
  }

  return (
    <Box className={classnames(css.nav)}>
      <Box paddingV={2}>
        <Divider />
      </Box>
      <BoxSummaryPickerContainer
        data-no-code="menuConfigBar"
        style={{ cursor: 'pointer' }}
        onClick={showBoxSummary}
        onKeyDown={(event) =>
          event.key === 'Enter' || event.key === ' ' ? showBoxSummary() : null
        }
        role="button"
        tabIndex={0}
        display={Display.Flex}
        flexDirection={FlexDirection.Row}
        alignItems={AlignItems.Center}
        justifyContent={
          isBasketComprehensionEnabled
            ? JustifyContent.SpaceBetween
            : [JustifyContent.SpaceBetween, null, JustifyContent.FlexStart]
        }
      >
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Row}
          justifyContent={JustifyContent.Center}
        >
          <BoxSummaryItem
            iconName="meals_per_box"
            itemTitle={title}
            dataNoCode="menuConfigBoxSize"
          />
          <BoxSummaryItem
            iconName="delivery"
            itemTitle={formattedBasketDate}
            dataNoCode="menuConfigDeliveryDate"
          />
        </Box>
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Row}
          justifyContent={JustifyContent.Center}
        >
          <Space direction="horizontal" size={8} />
          {isBasketComprehensionEnabled ? (
            <BoxSummaryBasketItem basketRecipesCount={basketRecipesCount} />
          ) : (
            <BoxSummaryEditItem />
          )}
        </Box>
      </BoxSummaryPickerContainer>
    </Box>
  )
}
